/* =======================================================================
   Header — Menu in alto (logo + voci di navigazione + lingua IT/EN)
   =======================================================================
   Per AGGIUNGERE/RIMUOVERE una voce dal menu in alto: modifica NAV_LINKS qui sotto.
   Per ogni voce serve:
     • to:  l'URL della pagina (deve corrispondere a una rotta in App.js)
     • key: la chiave del testo in src/data/copy.js → sezione "nav"
   ======================================================================= */
import { useLang } from "@/context/LanguageContext";
import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { asset } from "@/utils/asset";

const NAV_LINKS = [
  { to: "/", key: "home" },
  { to: "/storia", key: "story" },
  { to: "/menu", key: "menu" },
  { to: "/galleria", key: "gallery" },
  { to: "/contatti", key: "visit" },
];

export default function Header({ copy }) {
  const { lang, setLang, tr } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  // Header is "scrolled" (light) when not on Home, or when user scrolled down on Home.
  const onHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname]);

  const lightBg = !onHome || scrolled;

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        lightBg
          ? "bg-[#F7F4EB]/92 backdrop-blur-xl border-b border-[#D7D0B7]/60"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-3 flex items-center justify-between gap-6">
        <Link
          to="/"
          data-testid="brand-mark"
          className="relative flex items-center gap-3 group"
          aria-label="Le Simpatiche Canaglie® · Home"
        >
          <img
            src={asset("/assets/logo.jpg")}
            alt="Le Simpatiche Canaglie"
            className="h-16 sm:h-20 w-auto rounded-sm transition-transform duration-500 group-hover:scale-[1.03]"
          />
          <sup
            data-testid="brand-registered"
            className={`absolute -top-1 -right-3 text-[0.7rem] font-serif font-medium leading-none ${
              lightBg ? "text-[#BC472A]" : "text-[#E6C9A8]"
            }`}
            aria-label="marchio registrato"
          >
            ®
          </sup>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.key}`}
              className={({ isActive }) =>
                `relative text-[0.78rem] uppercase tracking-[0.22em] transition-colors ${
                  lightBg
                    ? "text-[#2C2C2A] hover:text-[#BC472A]"
                    : "text-[#F7F4EB] hover:text-[#E6C9A8]"
                } ${isActive ? "after:absolute after:left-0 after:right-0 after:-bottom-1.5 after:h-px after:bg-[#BC472A]" : ""}`
              }
            >
              {tr(copy.nav[l.key])}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            data-testid="lang-toggle"
            className={`flex items-center text-[0.78rem] tracking-[0.18em] uppercase border rounded-full overflow-hidden ${
              lightBg ? "border-[#2C2C2A]/30" : "border-[#F7F4EB]/50"
            }`}
          >
            <button
              data-testid="lang-it"
              onClick={() => setLang("it")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "it"
                  ? "bg-[#2C2C2A] text-[#F7F4EB]"
                  : lightBg
                    ? "text-[#2C2C2A]"
                    : "text-[#F7F4EB]"
              }`}
            >
              IT
            </button>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "en"
                  ? "bg-[#2C2C2A] text-[#F7F4EB]"
                  : lightBg
                    ? "text-[#2C2C2A]"
                    : "text-[#F7F4EB]"
              }`}
            >
              EN
            </button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden h-10 w-10 grid place-items-center border rounded-full ${
              lightBg ? "border-[#2C2C2A]/30" : "border-[#F7F4EB]/50"
            }`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            <div className="flex flex-col gap-[5px]">
              <span
                className={`block h-[1.5px] w-4 ${lightBg ? "bg-[#2C2C2A]" : "bg-[#F7F4EB]"} transition-transform ${open ? "translate-y-[6px] rotate-45" : ""}`}
              />
              <span
                className={`block h-[1.5px] w-4 ${lightBg ? "bg-[#2C2C2A]" : "bg-[#F7F4EB]"} transition-opacity ${open ? "opacity-0" : ""}`}
              />
              <span
                className={`block h-[1.5px] w-4 ${lightBg ? "bg-[#2C2C2A]" : "bg-[#F7F4EB]"} transition-transform ${open ? "-translate-y-[7px] -rotate-45" : ""}`}
              />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden overflow-hidden bg-[#F7F4EB]/95 backdrop-blur-xl border-t border-[#D7D0B7] transition-[max-height] duration-500 ${
          open ? "max-h-[420px]" : "max-h-0"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-4">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`m-nav-${l.key}`}
              className="text-left font-serif text-2xl text-[#2C2C2A] hover:text-[#BC472A] transition-colors"
            >
              {tr(copy.nav[l.key])}
            </NavLink>
          ))}
        </div>
      </div>
    </header>
  );
}
